import { useCallback, useEffect, useMemo, useState } from "react";
import { listExercises, createExercise, updateExercise, archiveExercise, listWods } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, PlayCircle, Pencil, Archive, Search, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const UNITS = [
  { value: "kg", label: "Weight (kg)" },
  { value: "reps", label: "Reps only" },
  { value: "time", label: "Time" },
  { value: "distance", label: "Distance" },
];

// Same rules as the server's name_key: case and extra spaces don't matter.
const nameKey = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const thumb = (video) => (video ? `https://img.youtube.com/vi/${video.id}/mqdefault.jpg` : null);
const watchUrl = (video) => `https://www.youtube.com/watch?v=${video.id}${video.start ? `&t=${video.start}s` : ""}`;
const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const empty = { name: "", aliases: "", video_url: "", cues: "", muscle_groups: "", equipment: "", default_unit: "kg" };

export default function ExerciseLibrary() {
  const [rows, setRows] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // null | {id?, ...form}
  const [saving, setSaving] = useState(false);
  const [wodNames, setWodNames] = useState([]);

  const load = useCallback(async () => {
    try {
      const d = await listExercises();
      setRows(d.exercises);
      setCanEdit(d.can_edit);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load the library");
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Exercise names used in WODs over the last ~7 weeks and the next week, to spot gaps in the library.
  useEffect(() => {
    const today = new Date();
    const from = new Date(today); from.setDate(from.getDate() - 48);
    const to = new Date(today); to.setDate(to.getDate() + 13);
    listWods(iso(from), iso(to)).then((d) => {
      const names = new Map();
      for (const w of d.wods || []) for (const b of w.blocks || []) for (const e of b.exercises || []) {
        const k = nameKey(e.name);
        if (!k) continue;
        const seen = names.get(k) || { name: e.name.trim(), count: 0 };
        seen.count += 1;
        names.set(k, seen);
      }
      setWodNames([...names.entries()]);
    }).catch(() => setWodNames([]));
  }, []);

  const keys = useMemo(() => new Set((rows || []).flatMap((r) => r.keys || [r.name_key])), [rows]);
  const missing = useMemo(
    () => wodNames.filter(([k]) => !keys.has(k)).map(([, v]) => v).sort((a, b) => b.count - a.count),
    [wodNames, keys],
  );
  const shown = useMemo(() => {
    const k = nameKey(q);
    return (rows || []).filter((r) => !k || (r.keys || [r.name_key]).some((x) => x.includes(k)));
  }, [rows, q]);

  const openNew = (name = "") => setEditing({ ...empty, name });
  const openEdit = (r) => setEditing({
    id: r.id, name: r.name, aliases: (r.aliases || []).join(", "), video_url: r.video_url || "", cues: r.cues || "",
    muscle_groups: (r.muscle_groups || []).join(", "), equipment: (r.equipment || []).join(", "),
    default_unit: r.default_unit || "kg",
  });

  const save = async () => {
    const f = editing;
    if (f.name.trim().length < 2) { toast.error("Give the exercise a name"); return; }
    const payload = {
      name: f.name.trim(), aliases: splitList(f.aliases), video_url: f.video_url.trim() || null,
      cues: f.cues.trim() || null, muscle_groups: splitList(f.muscle_groups), equipment: splitList(f.equipment),
      default_unit: f.default_unit,
    };
    setSaving(true);
    try {
      if (f.id) await updateExercise(f.id, payload); else await createExercise(payload);
      toast.success(f.id ? "Exercise updated" : "Exercise added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive ${r.name}? It disappears from the app; members keep their logged history.`)) return;
    try {
      await archiveExercise(r.id);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't archive");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="exercise-library">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Exercise Library</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Demo videos members see when they tap an exercise in the WOD. Shared by every gym; WOD exercises link
            here by name or alias.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => openNew()} data-testid="exercise-add">
            <Plus className="mr-1 h-4 w-4" /> Add exercise
          </Button>
        )}
      </div>

      {canEdit && missing.length > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4" data-testid="exercise-missing">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4" /> In recent WODs but not in the library (no video in the app yet)
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {missing.slice(0, 30).map((m) => (
              <button key={m.name} type="button" onClick={() => openNew(m.name)}
                className="text-xs rounded-full border border-zinc-700 px-3 py-1 hover:border-amber-400 hover:text-amber-200">
                {m.name} <span className="text-zinc-500">· {m.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercises or aliases" className="pl-9" />
      </div>

      {rows === null ? (
        <div className="text-sm text-zinc-400">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="text-sm text-zinc-400 border border-dashed border-zinc-700 rounded-lg p-8 text-center">
          {rows.length ? "No exercise matches that search." : "The library is empty. Add the movements your WODs use most, each with a YouTube demo."}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((r) => (
            <div key={r.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden" data-testid={`exercise-${r.id}`}>
              {r.video ? (
                <a href={watchUrl(r.video)} target="_blank" rel="noreferrer" className="block relative aspect-video bg-black">
                  <img src={thumb(r.video)} alt="" className="w-full h-full object-cover opacity-90" />
                  <PlayCircle className="absolute inset-0 m-auto h-12 w-12 text-white/90" />
                </a>
              ) : (
                <div className="aspect-video bg-zinc-900 flex items-center justify-center text-xs text-zinc-500">No video yet</div>
              )}
              <div className="p-4 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold">{r.name}</div>
                  {canEdit && (
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Archive" onClick={() => archive(r)}><Archive className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
                {r.aliases?.length > 0 && <div className="text-xs text-zinc-500">Also: {r.aliases.join(", ")}</div>}
                {r.cues && <div className="text-sm text-zinc-300">{r.cues}</div>}
                {(r.muscle_groups?.length > 0 || r.equipment?.length > 0) && (
                  <div className="text-xs text-zinc-500">{[...(r.muscle_groups || []), ...(r.equipment || [])].join(" · ")}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit exercise" : "Add exercise"}</DialogTitle>
            <DialogDescription>Paste the YouTube link from the video's Share button. A start time (e.g. ?t=15) is kept.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Name"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Back squat" /></Field>
              <Field label="Other names (comma separated)" hint="So WODs that write 'BS' or 'Back squats' still find it.">
                <Input value={editing.aliases} onChange={(e) => setEditing({ ...editing, aliases: e.target.value })} placeholder="BS, Back squats" />
              </Field>
              <Field label="YouTube link"><Input value={editing.video_url} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} placeholder="https://youtu.be/…" /></Field>
              <Field label="Coaching cues"><Textarea rows={3} value={editing.cues} onChange={(e) => setEditing({ ...editing, cues: e.target.value })} placeholder="Chest up, knees out, drive through the heels" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Muscle groups"><Input value={editing.muscle_groups} onChange={(e) => setEditing({ ...editing, muscle_groups: e.target.value })} placeholder="Legs, Glutes" /></Field>
                <Field label="Equipment"><Input value={editing.equipment} onChange={(e) => setEditing({ ...editing, equipment: e.target.value })} placeholder="Barbell" /></Field>
              </div>
              <Field label="Members log it as">
                <select className="w-full rounded-md border border-zinc-700 bg-transparent px-3 py-2 text-sm"
                  value={editing.default_unit} onChange={(e) => setEditing({ ...editing, default_unit: e.target.value })}>
                  {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-zinc-400">{label}</span>
      {children}
      {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
    </label>
  );
}
